import { useState } from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { Input } from "./Fields";
import { apiRequest, ApiError } from "../../api/client";

interface StepUpDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: (stepUpToken: string) => void;
  title?: string;
  description?: string;
}

/**
 * Used in front of every destructive or financially sensitive action
 * (bans, withdrawal decisions, admin role changes) — this is not just a
 * UI speed bump. The token it produces is checked and consumed server-side
 * (requireStepUp in the Worker); skipping this dialog and calling an
 * action's endpoint directly still gets rejected without a real token.
 */
export function StepUpDialog({
  isOpen,
  onClose,
  onVerified,
  title = "Confirm your password",
  description = "This action requires re-entering your password before it can proceed.",
}: StepUpDialogProps) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function submit() {
    if (!password) {
      setError("Enter your password");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await apiRequest<{ verified: boolean; stepUpToken: string }>("/admin/verify-password", {
        method: "POST",
        body: { password },
      });
      setPassword("");
      onVerified(result.stepUpToken);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not verify password");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose() {
    setPassword("");
    setError(null);
    onClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={submit} isLoading={isSubmitting}>
            Verify & Continue
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-ink-muted">{description}</p>
        <Input
          type="password"
          label="Your password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          error={error ?? undefined}
        />
      </div>
    </Modal>
  );
}
